import { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Spin, Typography, Space, Avatar } from 'antd';
import { UserOutlined, LockOutlined, ReadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { imageBaseUrl } from '../services/api';
import { useNotification } from '../context/NotificationContext';
// **IMPORTANT**: Make sure you have your logo file at this path
// or update the path to where your logo is located.
import schoolLogo from '../assets/logo.png';

const { Title, Text } = Typography;

const LoginPage = () => {
  const { message: notify } = useNotification();
  const [loading, setLoading] = useState(false);
  const [schoolSettings, setSchoolSettings] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Fetch school settings
  useEffect(() => {
    const fetchSchoolSettings = async () => {
      try {
        const response = await api.get('/settings');
        setSchoolSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch school settings:', error);
      }
    };
    fetchSchoolSettings();
  }, []);

  const schoolName = schoolSettings?.school_name || 'School Management System';
  const schoolLogoUrl = schoolSettings?.logo_url ? `${imageBaseUrl}${schoolSettings.logo_url}` : schoolLogo;

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/auth/login', {
        username: values.username.trim(),
        password: values.password.trim(),
      });

      if (response.data.token) {
        login(response.data.token);
        notify.success('Login successful!');
        navigate('/'); // Redirect to the dashboard
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Login failed. Please check your credentials.';
      notify.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      padding: '24px',
      backgroundColor: '#F8FAFC' // Modern slate background
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          borderRadius: '16px',
          padding: '24px 12px',
        }}
        bordered={false}
      >
        <Spin spinning={loading}>
          <Space direction="vertical" align="center" style={{ width: '100%', marginBottom: 32 }}>
            {/* School Logo */}
            <div style={{ padding: 12, backgroundColor: '#EFF6FF', borderRadius: '20px' }}>
                <Avatar src={schoolLogoUrl} size={72} icon={<ReadOutlined />} style={{ backgroundColor: 'transparent' }} />
            </div>

            {/* School Name */}
            <div style={{ textAlign: 'center' }}>
                <Title level={3} style={{ margin: '8px 0 4px 0', fontWeight: 800, color: '#1E293B' }}>
                {schoolName}
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>Academic Management Portal</Text>
            </div>
          </Space>

          <Form name="login_form" onFinish={onFinish} size="large" layout="vertical">
            <Form.Item
              name="username"
              rules={[{ required: true, message: 'Username is required' }]}
              label={<Text strong style={{ fontSize: 13 }}>Username</Text>}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#64748B' }} />}
                placeholder="Enter your username"
                style={{ borderRadius: '8px' }}
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Password is required' }]}
              label={<Text strong style={{ fontSize: 13 }}>Password</Text>}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#64748B' }} />}
                placeholder="Enter your password"
                style={{ borderRadius: '8px' }}
              />

            </Form.Item>
            <Form.Item style={{ marginTop: 32 }}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: '100%', height: '48px', borderRadius: '8px', fontSize: 16, fontWeight: 700 }}
                loading={loading}
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

export default LoginPage;